import React            from "react";
import Box              from "@mui/material/Box";
import TextField        from "@mui/material/TextField";
import Button           from "@mui/material/Button";
import CircularProgress from '@mui/material/CircularProgress';
import Typography       from "@mui/material/Typography";
import {
    useForm,
    Controller,
    SubmitHandler
}                       from "react-hook-form";
import { useNavigate }  from "react-router-dom";
import FormLayout       from "ui/components/FormLayout";
import useQueryParam    from "hooks/useQueryParam";
import { HttpClient, ApiError } from "apiclient/ApiClient";
import validateURI      from 'utils/validateURI';
import NEW_TEACHER_IMAGE from 'images/new_teacher_ill.png';
import {
    FORM_NEW_TEACHER_ERROR,
    FORM_NEW_TEACHER_FIRST_NAME,
    FORM_NEW_TEACHER_HEADER,
    FORM_NEW_TEACHER_HELPER,
    FORM_NEW_TEACHER_LAST_NAME,
    FORM_NEW_TEACHER_ROLE,
    FORM_NEW_TEACHER_SHOOL,
    FORM_NEW_TEACHER_SHOOL_ADDRESS,
    FORM_NEW_TEACHER_SHOOL_WEBSITE,
    FORM_NEW_TEACHER_SUBHEADER,
    FORM_NEW_TEACHER_SUBMIT,
    WRONG_URI_ERROR
}                       from 'config/config';
import {
    useAuth
}                       from 'auth/AuthProvider';
import { Backdrop } from "@mui/material";

interface IFormInput {
    first_name: string;
    last_name: string;
    school: string;
    position: string;
    school_address: string;
    school_site?: string;
};

export default function FormNewTeacher() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(false);
    const [checking, setChecking] = React.useState(true);
    const { control, handleSubmit, formState: { errors } } = useForm<IFormInput>();
    const [error, setError] = React.useState('')
    const [submitting, setSubmitting] = React.useState(false);
    const auth = useAuth();
    const token = useQueryParam('access_token');
    React.useEffect(()=>{
        if (token) {
            auth.signin.withAccessToken(token)
        };
    },[token, auth])
    
    // Redirect to signin if no session and no token
    React.useEffect(() => {
        if (!auth.session && !token && !auth.loading) {
            navigate('/signin', { replace: true });
        }
    }, [auth.session, token, auth.loading, navigate]);
    
    const client = HttpClient();
    React.useEffect(()=>{
        const checkProfile = async () => {
            const resp = await client.get('/teacher_profile_exists');
            setChecking(false);
            // Only redirect if user has both a profile AND a visible_name
            // Otherwise they should fill out the form to set/update their name
            if (resp && (resp as unknown as string) === 'YES' && 
                auth.session?.visible_name && auth.session.visible_name.trim() !== '') {
                navigate('/classes');
            }
        }
        if (!auth.session && submitting) {
            return navigate('/signin');
        }
        if (auth.session && !submitting) {
            checkProfile();
        };
    },[client, navigate, auth.session, submitting]);
    const onSubmit: SubmitHandler<IFormInput> = async data => {
        setSubmitting(true)
        setError('');
        setLoading(true);
        try {
            await client.post('/teacher/profile/create', data);
            if (auth.session) {
                // Update session with visible_name from form data
                const updatedSession = {
                    ...auth.session,
                    visible_name: `${data.first_name} ${data.last_name}`.trim()
                };
                auth.setSession(updatedSession);
                
                let win = (window || global) as any;
                win.fbq('track', 'CompleteRegistration');
                navigate('/create-class')
            } else {
                setError(FORM_NEW_TEACHER_ERROR)
            }
        } catch (err) {
            if (err instanceof ApiError) {
                setError(err.message || FORM_NEW_TEACHER_ERROR);
            } else {
                setError(FORM_NEW_TEACHER_ERROR);
            }
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };
    if (checking) {
        return (
            <Backdrop open={checking} sx={{ zIndex: 2000, backdropFilter: 'brightness(100%)', backgroundColor: 'transparent' }}>
                <CircularProgress color="primary" />
            </Backdrop>
        );
    }
    return(
        
        // <RequireAuth>
            <FormLayout hideProfileMenu={true}>
                <Typography variant="h6" mb={2} textAlign='center'>
                    {FORM_NEW_TEACHER_HEADER}
                </Typography>
                <Box
                    component="img"
                    src={NEW_TEACHER_IMAGE}
                    alt=""
                    sx={{ width: 220, maxWidth: '100%', height: 'auto' }}
                />
                <Typography variant='body1' textAlign='center'>
                    {FORM_NEW_TEACHER_SUBHEADER}
                </Typography>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box
                        display='flex'
                        sx={{gap: 4, flexDirection: 'column', mt: 4}}
                        width="100%"
                    >
                        <Controller
                            name="first_name"
                            control={control}
                            defaultValue=""
                            render={({field}) =>
                                <TextField
                                    fullWidth
                                    label={FORM_NEW_TEACHER_FIRST_NAME}
                                    required
                                    {...field}
                                />
                            }
                        />
                        <Controller
                            name="last_name"
                            control={control}
                            defaultValue=""
                            render={({field}) =>
                                <TextField
                                    fullWidth
                                    label={FORM_NEW_TEACHER_LAST_NAME}
                                    required
                                    {...field}
                                />
                            }
                        />
                        <Controller
                            name="position"
                            control={control}
                            defaultValue=""
                            render={({field}) =>
                                <TextField
                                    fullWidth
                                    label={FORM_NEW_TEACHER_ROLE}
                                    required
                                    {...field}
                                />
                            }
                        />
                        <Controller
                            name="school"
                            control={control}
                            defaultValue=""
                            render={({field}) =>
                                <TextField
                                    fullWidth
                                    label={FORM_NEW_TEACHER_SHOOL}
                                    required
                                    {...field}
                                />
                            }
                        />
                        <Controller
                            name="school_address"
                            control={control}
                            defaultValue=""
                            render={({field}) =>
                                <TextField
                                    fullWidth
                                    label={FORM_NEW_TEACHER_SHOOL_ADDRESS}
                                    required
                                    {...field}
                                />
                            }
                        />

                        <Box>
                            <Controller
                                name="school_site"
                                control={control}
                                defaultValue=""
                                rules={{
                                    validate: {
                                        checkUrl: v => v ? validateURI(v) || WRONG_URI_ERROR : true,
                                    }
                                }}
                                render={({field}) =>
                                    <TextField
                                        fullWidth
                                        error={errors.school_site?.message ? true : false}
                                        helperText={errors.school_site?.message}
                                        label={FORM_NEW_TEACHER_SHOOL_WEBSITE}
                                        {...field}
                                    />
                                }
                            />
                            <Typography variant='caption' color='secondary'>
                                {FORM_NEW_TEACHER_HELPER}
                            </Typography>
                        </Box>
                        <Box>
                            <Button
                                variant='contained'
                                fullWidth
                                disabled={loading}
                                type='submit'
                                color={error ? 'error' : 'primary'}
                                sx={{mb: 2, boxShadow: 'none'}}
                            >
                                {FORM_NEW_TEACHER_SUBMIT}
                            </Button>
                            {error &&
                                <Typography align="center" color="error">
                                    {error}
                                </Typography>
                            }
                        </Box>
                        {loading &&
                            <CircularProgress sx={{alignSelf: 'center'}}/>
                        }
                    </Box>
                </form>
            </FormLayout>
        // </RequireAuth>
    );
};